/**
 * Itinerary Admin Panel
 * Administrator actions for Coordination Itinerary course reset workflows.
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  alpha,
  Stack,
} from '@mui/material';
import {
  DeleteForever as DeleteForeverIcon,
  CheckCircle as CheckIcon,
  ArchiveOutlined as ArchiveIcon,
  PersonOffOutlined as PersonOffIcon,
  RestartAlt as RestartIcon,
} from '@mui/icons-material';
import InfoHint from '../common/InfoHint';
import * as itineraryApi from '../../services/itineraryApi';
import { useParticipantLabels } from '../../hooks/useParticipantLabels';
import { useStudentProfileStore } from '@signals/coordination-itinerary-ui/stores/studentProfileStore';

type CourseFamily = 'DHOST' | 'DHOQC';

const COURSE_FAMILY_OPTIONS: { value: CourseFamily; label: string; blurb: string }[] = [
  { value: 'DHOST', label: 'DHOST', blurb: 'Every DHOST iteration (e.g. 26-002)' },
  { value: 'DHOQC', label: 'DHOQC', blurb: 'Every DHOQC iteration (e.g. 26-002)' },
];

const ItineraryAdminPanel: React.FC = () => {
  const { singular, singularLower } = useParticipantLabels();
  const clearAllProfiles = useStudentProfileStore((state) => state.clearAllProfiles);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [wipeDialogOpen, setWipeDialogOpen] = useState(false);
  const [wipingProfiles, setWipingProfiles] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const hasCourseSelection =
    selectedCourseId === 'DHOST' || selectedCourseId === 'DHOQC';

  const selectedLabel =
    COURSE_FAMILY_OPTIONS.find((option) => option.value === selectedCourseId)?.label ?? '';

  const confirmationTitle = hasCourseSelection
    ? `Remove ${singular} Profile Data for ${selectedLabel} courses`
    : `Remove ${singular} Profile Data`;

  const handleWipeAllProfiles = async () => {
    if (!hasCourseSelection) return;

    setWipingProfiles(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await itineraryApi.deleteAllStudentProfiles(selectedCourseId as CourseFamily);
      clearAllProfiles();
      setWipeDialogOpen(false);
      setSuccess(
        `${result.message} (${result.course_id} courses, ${result.students_affected} ${singularLower}(s) affected) ${result.profiles_deleted} profile(s) deleted, ${result.profiles_cleared} cleared, ${result.itineraries_archived} itinerary(s) archived, ${result.observations_archived} observation(s) archived.`
      );
    } catch (err: any) {
      console.error('Error wiping student profiles:', err);
      setError(err.response?.data?.detail || 'Failed to remove student profile data.');
    } finally {
      setWipingProfiles(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
        <Typography variant="overline" sx={{ letterSpacing: 1.2, color: 'text.secondary', fontWeight: 700 }}>
          Between courses
        </Typography>
        <InfoHint title="Course reset">
          Wipes Coordination Itinerary profiles for every iteration of a course family
          (DHOST or DHOQC), based on who is assigned in Device Reference. Itineraries and
          observations are archived — hidden from the live views, still in the database.
          Affected people must re-enter their information next time.
        </InfoHint>
      </Stack>
      <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: -0.4, mb: 2.5 }}>
        Reset {singularLower} profiles
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }} icon={<CheckIcon />}>
          {success}
        </Alert>
      )}

      <Box
        sx={{
          borderRadius: 2.5,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.error.main, isDark ? 0.45 : 0.35)}`,
          background: isDark
            ? `linear-gradient(165deg, ${alpha(theme.palette.error.main, 0.12)} 0%, ${alpha('#140808', 0.9)} 55%)`
            : `linear-gradient(165deg, ${alpha(theme.palette.error.main, 0.06)} 0%, ${theme.palette.background.paper} 60%)`,
        }}
      >
        <Box sx={{ px: 2.5, pt: 2.25, pb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.75 }}>
            What this does
          </Typography>
          <Stack spacing={1.25}>
            {[
              { icon: <PersonOffIcon fontSize="small" />, text: `Deletes stored ${singularLower} admin and vehicle profile data` },
              { icon: <ArchiveIcon fontSize="small" />, text: 'Archives itinerary and observation submissions (not destroyed)' },
              { icon: <RestartIcon fontSize="small" />, text: `Those ${singularLower}s start Coordination Itinerary from a blank form` },
            ].map((row) => (
              <Stack key={row.text} direction="row" spacing={1.25} alignItems="flex-start">
                <Box sx={{ color: 'error.light', mt: 0.15, opacity: 0.9 }}>{row.icon}</Box>
                <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
                  {row.text}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>

        <Box sx={{ px: 2.5, pb: 2.5, pt: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.6, color: 'text.secondary', display: 'block', mb: 1 }}>
            COURSE FAMILY
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
            {COURSE_FAMILY_OPTIONS.map((option) => {
              const on = selectedCourseId === option.value;
              return (
                <Box
                  key={option.value}
                  role="button"
                  tabIndex={0}
                  onClick={() => !wipingProfiles && setSelectedCourseId(option.value)}
                  onKeyDown={(e) => {
                    if (wipingProfiles) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedCourseId(option.value);
                    }
                  }}
                  sx={{
                    flex: 1,
                    px: 1.75,
                    py: 1.35,
                    borderRadius: 1.5,
                    cursor: wipingProfiles ? 'default' : 'pointer',
                    border: 1,
                    borderColor: on ? 'error.main' : 'divider',
                    bgcolor: on ? alpha(theme.palette.error.main, isDark ? 0.18 : 0.08) : 'background.paper',
                    transition: 'border-color .15s, background-color .15s',
                    outline: 'none',
                    '&:focus-visible': { boxShadow: `0 0 0 2px ${theme.palette.error.main}` },
                  }}
                >
                  <Typography sx={{ fontWeight: 800, letterSpacing: 0.4 }}>{option.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.blurb}
                  </Typography>
                </Box>
              );
            })}
          </Stack>

          <Button
            variant="contained"
            color="error"
            startIcon={wipingProfiles ? <CircularProgress size={18} color="inherit" /> : <DeleteForeverIcon />}
            onClick={() => setWipeDialogOpen(true)}
            disabled={wipingProfiles || !hasCourseSelection}
            sx={{ textTransform: 'none', fontWeight: 700, px: 2 }}
          >
            Remove {singular} Profile Data
          </Button>
        </Box>
      </Box>

      <Dialog open={wipeDialogOpen} onClose={() => !wipingProfiles && setWipeDialogOpen(false)}>
        <DialogTitle>{confirmationTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently remove admin and vehicle data for {singularLower}s enrolled in any{' '}
            <strong>{selectedLabel}</strong> course (for example {selectedLabel} 26-002),
            based on device reference assignments. Their itinerary and observation submissions will be archived.
            Archived submissions no longer appear in View Itineraries or View Observations, but remain in the database
            for later retrieval. Affected students will need to re-enter their information in Coordination Itinerary.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWipeDialogOpen(false)} disabled={wipingProfiles}>
            Cancel
          </Button>
          <Button
            onClick={handleWipeAllProfiles}
            color="error"
            variant="contained"
            disabled={wipingProfiles || !hasCourseSelection}
            startIcon={wipingProfiles ? <CircularProgress size={18} color="inherit" /> : <DeleteForeverIcon />}
          >
            {wipingProfiles ? 'Removing...' : confirmationTitle}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ItineraryAdminPanel;
